export type DecisionStatus = 'Open' | 'Needs Discovery' | 'Pending Client' | 'Decided'
export type DecisionPriority = 'Critical' | 'High' | 'Medium'

export type Decision = {
  id: string
  decisionId: string
  workstream: string
  question: string
  whyItMatters: string
  recommendedStart: string
  finalDecision: string
  owner: string
  priority: DecisionPriority
  /** Sort key so Firestore can order by severity without a client-side pass. */
  priorityRank: number
  status: DecisionStatus
  targetDate: string
  dependencies: string
  source: string
  unnumberedInSource?: boolean
  answeredBy?: string | null
  answeredAt?: { seconds: number } | null
  updatedAt?: { seconds: number } | null
}

export type ActivityEntry = {
  id: string
  kind: 'answer' | 'comment' | 'status'
  body: string
  byEmail: string
  byName: string | null
  /** Id of the entry this replies to. Null or absent for a top-level entry. */
  parentId?: string | null
  at: { seconds: number } | null
}

export type ActivityThread = ActivityEntry & { replies: ActivityEntry[] }

/**
 * Groups a flat activity list into threads: newest conversation first, but replies within
 * a thread oldest first, because a thread is read downwards while a log is scanned from
 * the top. A reply whose parent has vanished is promoted rather than dropped — losing
 * someone's comment because of a dangling reference is worse than showing it out of place.
 */
export function toThreads(entries: ActivityEntry[]): ActivityThread[] {
  const byId = new Map(entries.map((e) => [e.id, e]))
  const replies = new Map<string, ActivityEntry[]>()
  const roots: ActivityEntry[] = []

  for (const e of entries) {
    const parent = e.parentId && byId.has(e.parentId) ? e.parentId : null
    if (parent) {
      const list = replies.get(parent) ?? []
      list.push(e)
      replies.set(parent, list)
    } else {
      roots.push(e)
    }
  }

  const seconds = (e: ActivityEntry) => e.at?.seconds ?? 0
  return roots.map((r) => ({
    ...r,
    replies: (replies.get(r.id) ?? []).sort((a, b) => seconds(a) - seconds(b)),
  }))
}

export const PRIORITY_RANK: Record<DecisionPriority, number> = {
  Critical: 0, High: 1, Medium: 2,
}

export const STATUSES: DecisionStatus[] = ['Open', 'Needs Discovery', 'Pending Client', 'Decided']
export const PRIORITIES: DecisionPriority[] = ['Critical', 'High', 'Medium']

export const isOpen = (d: Decision) => d.status !== 'Decided'

/** Past its target date and still unresolved. Drives the home-page risk flag. */
export const isOverdue = (d: Decision, today = new Date()) => {
  if (!d.targetDate || !isOpen(d)) return false
  const target = new Date(`${d.targetDate}T23:59:59`)
  return !Number.isNaN(target.valueOf()) && target < today
}
